import React from 'react'
import { RING_DEFS, LEVEL_LABELS } from '../lib/geometry'
import { BODY_FONT } from '../lib/palette'
import { primaryBtn, secondaryBtn, dangerBtn, toggleBtn } from '../lib/styles'

export const emptyForm = cell => ({
    formSide: cell ? cell.side : 'future',
    formRing: cell ? cell.ring : 0,
    formValence: cell ? cell.valence : 'positive',
    formLevel: cell ? cell.level : 0,
    formTitle: '',
    formNote: '',
    formDate: '',
})

const sideNamesFor = side => RING_DEFS.map(rd => (side === 'future' ? rd.futureLabel : rd.pastLabel))

const ReflectionForm = ({ palette, form, onFormChange, mode, error, itemTitle, onSave, onCancel, onDelete }) => {
    const styles = {
        formGroup: { marginBottom: '16px' },
        formLabel: {
            display: 'block',
            fontSize: '11px',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: palette.sub,
            marginBottom: '8px',
        },
        toggleRow: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
        formInput: {
            width: '100%',
            fontFamily: BODY_FONT,
            fontSize: '14px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: `1px solid ${palette.line}`,
            boxSizing: 'border-box',
            background: 'transparent',
            color: palette.ink,
        },
        formTextarea: {
            width: '100%',
            fontFamily: BODY_FONT,
            fontSize: '14px',
            padding: '10px 12px',
            borderRadius: '8px',
            border: `1px solid ${palette.line}`,
            boxSizing: 'border-box',
            minHeight: '70px',
            resize: 'vertical',
            background: 'transparent',
            color: palette.ink,
        },
        errorText: {
            fontSize: '13px',
            fontWeight: 500,
            color: `oklch(45% 0.15 ${palette.negHue})`,
            margin: '0 0 10px',
        },
        actions: { display: 'flex', gap: '10px', marginTop: '10px', flexWrap: 'wrap' },
        primaryBtn: primaryBtn(palette),
        secondaryBtn: secondaryBtn(palette),
        dangerBtn: dangerBtn(palette),
    }

    const sideOptions = ['future', 'past'].map(v => ({
        value: v,
        label: v === 'future' ? 'Future' : 'Past',
        selected: form.formSide === v,
        tight: false,
        onClick: () => onFormChange(f => ({ ...f, formSide: v })),
    }))
    const ringOptions = sideNamesFor(form.formSide).map((label, i) => ({
        value: i,
        label,
        selected: form.formRing === i,
        tight: false,
        onClick: () => onFormChange(f => ({ ...f, formRing: i })),
    }))
    const valenceOptions = ['positive', 'negative'].map(v => ({
        value: v,
        label: v === 'positive' ? 'Positive' : 'Negative',
        selected: form.formValence === v,
        tight: false,
        onClick: () => onFormChange(f => ({ ...f, formValence: v })),
    }))
    const levelOptions = LEVEL_LABELS.map((label, i) => ({
        value: i,
        label,
        selected: form.formLevel === i,
        tight: true,
        onClick: () => onFormChange(f => ({ ...f, formLevel: i })),
    }))

    const groups = [
        { id: 'reflection-group-when', label: 'When', options: sideOptions },
        { id: 'reflection-group-distance', label: 'Distance', options: ringOptions },
        { id: 'reflection-group-valence', label: 'Valence', options: valenceOptions },
        { id: 'reflection-group-intensity', label: 'Intensity', options: levelOptions },
    ]
    const fields = [
        {
            id: 'reflection-field-title',
            label: 'Title',
            key: 'formTitle',
            placeholder: 'What happened, or what might?',
            textarea: false,
        },
        {
            id: 'reflection-field-note',
            label: 'Note',
            key: 'formNote',
            placeholder: 'A line or two of context',
            textarea: true,
        },
        {
            id: 'reflection-field-timing',
            label: 'Timing',
            key: 'formDate',
            placeholder: 'e.g. Next spring, 2021',
            textarea: false,
        },
    ]

    return (
        <>
            {groups.map(g => (
                <div key={g.id} style={styles.formGroup}>
                    <span id={g.id} style={styles.formLabel}>
                        {g.label}
                    </span>
                    <div style={styles.toggleRow} role="radiogroup" aria-labelledby={g.id}>
                        {g.options.map(o => (
                            <button
                                key={o.value}
                                style={toggleBtn(o.selected, palette, o.tight)}
                                role="radio"
                                aria-checked={o.selected}
                                onClick={o.onClick}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                </div>
            ))}
            {fields.map(fd => (
                <div key={fd.id} style={styles.formGroup}>
                    <label htmlFor={fd.id} style={styles.formLabel}>
                        {fd.label}
                    </label>
                    {fd.textarea ? (
                        <textarea
                            id={fd.id}
                            style={styles.formTextarea}
                            value={form[fd.key]}
                            onChange={e => {
                                const value = e.target.value
                                onFormChange(f => ({ ...f, [fd.key]: value }))
                            }}
                            placeholder={fd.placeholder}
                        />
                    ) : (
                        <input
                            id={fd.id}
                            style={styles.formInput}
                            value={form[fd.key]}
                            onChange={e => {
                                const value = e.target.value
                                onFormChange(f => ({ ...f, [fd.key]: value }))
                            }}
                            placeholder={fd.placeholder}
                        />
                    )}
                </div>
            ))}
            {error && (
                <p style={styles.errorText} role="alert">
                    {error}
                </p>
            )}
            <div style={styles.actions}>
                <button style={styles.primaryBtn} onClick={onSave}>
                    {mode === 'edit' ? 'Save changes' : 'Add reflection'}
                </button>
                <button style={styles.secondaryBtn} onClick={onCancel}>
                    Cancel
                </button>
                {mode === 'edit' && (
                    <button style={styles.dangerBtn} onClick={onDelete} aria-label={`Delete reflection: ${itemTitle}`}>
                        Delete
                    </button>
                )}
            </div>
        </>
    )
}

export default ReflectionForm
